// nested_fields_controller.js
// Replaces Cocoon gem for dynamic nested forms
//
// Usage:
//   <div data-controller="nested-fields">
//     <!-- Template (hidden) -->
//     <template data-nested-fields-target="template">
//       <!-- Your nested form fields here -->
//       <!-- Use NEW_RECORD as placeholder for unique IDs -->
//     </template>
//
//     <!-- Container for nested items -->
//     <div data-nested-fields-target="container">
//       <!-- Existing nested items will be here -->
//     </div>
//
//     <!-- Add button -->
//     <button data-action="click->nested-fields#add">Add Item</button>
//   </div>
//
// Each nested item should have:
//   - data-nested-fields-target="item"
//   - Remove button with data-action="click->nested-fields#remove"

import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

export default class extends Controller {
  static targets = ["template", "container", "item"]
  static values = {
    wrapperClass: { type: String, default: "nested-fields" }
  }

  connect() {
    this.wrapperClass = this.wrapperClassValue
    this.index = new Date().getTime() // Unique timestamp for new records
  }

  add(event) {
    event.preventDefault()

    if (!this.hasTemplateTarget) {
      logger.error("NestedFields: No template found")
      return
    }

    // Clone the template
    const content = this.templateTarget.content.cloneNode(true)
    const firstChild = content.firstElementChild

    if (!firstChild) {
      logger.error("NestedFields: Template has no content")
      return
    }

    // Handle table rows specially - they can't be wrapped in divs
    const isTableRow = firstChild.tagName === 'TR'
    let element

    if (isTableRow) {
      // For table rows, clone directly and replace IDs in the HTML string before cloning
      const htmlString = this.replaceIds(firstChild.outerHTML)
      const tempContainer = document.createElement('tbody')
      tempContainer.innerHTML = htmlString
      element = tempContainer.querySelector('tr')
    } else {
      // For other elements, use the wrapper div approach
      const wrapper = document.createElement('div')
      wrapper.innerHTML = this.replaceIds(firstChild.outerHTML)
      element = wrapper.firstElementChild
    }

    if (!element) {
      logger.error("NestedFields: Failed to create element")
      return
    }

    // Add wrapper class
    element.classList.add(this.wrapperClass)
    element.dataset.nestedFieldsTarget = "item"

    // Insert into container
    if (this.hasContainerTarget) {
      this.containerTarget.appendChild(element)
    } else {
      this.templateTarget.parentElement.insertBefore(element, this.templateTarget)
    }

    // Trigger custom event for animations/callbacks
    this.element.dispatchEvent(new CustomEvent('nested-fields:added', {
      bubbles: true,
      detail: { item: element, index: this.index }
    }))

    // Increment index for next item
    this.index++
  }

  remove(event) {
    event.preventDefault()

    const button = event.currentTarget
    const item = button.closest('[data-nested-fields-target="item"]')

    if (!item) {
      logger.error("NestedFields: Remove button not inside an item")
      return
    }

    // Check for confirmation message
    const confirmMessage = button.dataset.turboConfirm || button.dataset.confirm

    if (confirmMessage) {
      // Use SweetAlert2 if available, otherwise native confirm
      const confirmed = typeof window.Swal !== 'undefined'
        ? this.confirmWithSwal(confirmMessage)
        : Promise.resolve(window.confirm(confirmMessage))

      confirmed.then(result => {
        if (result) {
          this.performRemove(button, item)
        }
      })
    } else {
      // No confirmation needed
      this.performRemove(button, item)
    }
  }

  confirmWithSwal(message) {
    return window.Swal.fire({
      text: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '<i class="fa fa-check"></i> אישור',
      cancelButtonText: '<i class="fa fa-times"></i> ביטול',
      customClass: {
        confirmButton: 'btn btn-danger me-2',
        cancelButton: 'btn btn-secondary ms-2',
        actions: 'd-flex gap-2 justify-content-center'
      },
      buttonsStyling: false,
      reverseButtons: true  // Show cancel on right (RTL layout)
    }).then((result) => result.isConfirmed)
  }

  performRemove(button, item) {
    // Check if this is a persisted record (has ID)
    const idField = item.querySelector('input[name*="[id]"]')
    const isPersisted = idField && idField.value

    logger.log('🗑️ Removing nested field:', {
      idField: idField ? idField.name : 'not found',
      idValue: idField ? idField.value : 'n/a',
      isPersisted: isPersisted,
      itemHTML: item.outerHTML.substring(0, 200)
    })

    if (isPersisted) {
      // Mark for deletion instead of removing
      let destroyField = item.querySelector('input[name*="[_destroy]"]')

      if (!destroyField) {
        // Create _destroy field if it doesn't exist
        destroyField = document.createElement('input')
        destroyField.type = 'hidden'
        const name = idField.name.replace('[id]', '[_destroy]')
        destroyField.name = name
        item.appendChild(destroyField)
        logger.log('✓ Created _destroy field:', destroyField.name)
      }

      destroyField.value = '1'
      logger.log('✓ Set _destroy = 1, hiding item')

      // Hide the item
      item.style.display = 'none'
    } else {
      // Remove from DOM (not persisted yet)
      logger.log('✓ Removing from DOM (not persisted)')
      item.remove()
    }

    // Trigger custom event
    this.element.dispatchEvent(new CustomEvent('nested-fields:removed', {
      bubbles: true,
      detail: { item: item, persisted: isPersisted }
    }))
  }

  replaceIds(content) {
    // Replace NEW_RECORD placeholder with unique timestamp
    const timestamp = this.index
    return content.replace(/NEW_RECORD/g, timestamp)
  }

  // Handle unique field IDs for form fields (href="#field" pattern)
  itemTargetConnected(item) {
    const uniqueTime = Math.floor(Date.now())

    // Update href="#field" links to be unique
    item.querySelectorAll('a[href="#field"]').forEach(link => {
      link.setAttribute('href', `#field${uniqueTime}`)
    })

    // Update matching id="field" elements
    item.querySelectorAll('#field').forEach(field => {
      field.setAttribute('id', `field${uniqueTime}`)
    })
  }
}
